import React, { createElement, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import AssignDualListPanels from '@core/components/assign/AssignDualListPanels';
import AssignAvailableRow from '@core/components/assign/AssignAvailableRow';
import AssignAssignedRow from '@core/components/assign/AssignAssignedRow';
import { resolveAssignedRowDisplay } from '@core/components/assign/assignDisplay';
import { todayDateOnly, toDateOnlyInputValue } from '@core/utils/formatDateOnly';
import { AssignReportTemplatesScope } from '../types/reportTemplateAssignment';
import { UseAssignReportTemplatesReturn } from '../hooks/useAssignReportTemplates';
import PreviewReportTemplateModal from './previewReportTemplateModal';

const SCOPE_COPY: Record<
  AssignReportTemplatesScope,
  {
    originLabel: string;
    availableTitle: string;
    assignedTitle: string;
    availableEmptyText: string;
    assignedEmptyText: string;
  }
> = {
  CHURCH: {
    originLabel: 'church',
    availableTitle: 'Available (church origin)',
    assignedTitle: 'Assigned',
    availableEmptyText: 'No more church-origin templates to assign.',
    assignedEmptyText: 'No templates assigned.',
  },
  DISTRICT: {
    originLabel: 'district',
    availableTitle: 'Available (district origin)',
    assignedTitle: 'Assigned to district',
    availableEmptyText: 'No more district-origin templates to assign.',
    assignedEmptyText: 'No templates assigned to this district.',
  },
  NATION: {
    originLabel: 'nation',
    availableTitle: 'Available (nation origin)',
    assignedTitle: 'Assigned to nation',
    availableEmptyText: 'No more nation-origin templates to assign.',
    assignedEmptyText: 'No templates assigned to this nation.',
  },
};

type Props = Pick<
  UseAssignReportTemplatesReturn,
  | 'departments'
  | 'loadingDepartments'
  | 'selectedDepartmentId'
  | 'setSelectedDepartmentId'
  | 'saving'
  | 'isLoading'
  | 'qLeft'
  | 'setQLeft'
  | 'qRight'
  | 'setQRight'
  | 'left'
  | 'visibleAttached'
  | 'attached'
  | 'assignTemplate'
  | 'unassignTemplate'
  | 'handleOrderChange'
  | 'updateAttached'
  | 'activeTemplateIds'
  | 'activeTemplateLookup'
  | 'scope'
  | 'entityId'
>;

const AssignReportTemplatesModalBody: React.FC<Props> = (props) => {
  const {
    departments,
    loadingDepartments,
    selectedDepartmentId,
    setSelectedDepartmentId,
    saving,
    isLoading,
    qLeft,
    setQLeft,
    qRight,
    setQRight,
    left,
    visibleAttached,
    attached,
    assignTemplate,
    unassignTemplate,
    handleOrderChange,
    updateAttached,
    activeTemplateIds,
    activeTemplateLookup,
    scope,
    entityId,
  } = props;

  const [previewTemplateId, setPreviewTemplateId] = useState<number | null>(null);
  const copy = SCOPE_COPY[scope];

  const previewScopeProps =
    scope === 'CHURCH'
      ? { churchId: entityId }
      : scope === 'DISTRICT'
        ? { districtId: entityId }
        : { nationId: entityId };

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold small">Department</Form.Label>
        <Form.Select
          size="sm"
          value={selectedDepartmentId}
          onChange={(e) => setSelectedDepartmentId(Number(e.target.value))}
          disabled={loadingDepartments || saving}
        >
          {loadingDepartments && <option value={0}>Loading departments...</option>}
          {!loadingDepartments && departments.length === 0 && (
            <option value={0}>No departments found</option>
          )}
          {departments.map((dept) => (
            <option key={dept.department_id} value={dept.department_id}>
              {dept.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : !selectedDepartmentId ? (
        <div className="text-muted text-center py-4">
          Please select a department to manage template assignments.
        </div>
      ) : (
        <AssignDualListPanels
          availableTitle={copy.availableTitle}
          assignedTitle={copy.assignedTitle}
          availableCount={left.length}
          assignedCount={attached.length}
          availableSearchPlaceholder="Search..."
          assignedSearchPlaceholder="Search..."
          availableSearch={qLeft}
          assignedSearch={qRight}
          onAvailableSearchChange={setQLeft}
          onAssignedSearchChange={setQRight}
          availableEmptyText={copy.availableEmptyText}
          assignedEmptyText={copy.assignedEmptyText}
          availableIsEmpty={left.length === 0}
          assignedIsEmpty={visibleAttached.length === 0}
          availableItems={left.map((t) =>
            createElement(AssignAvailableRow, {
              key: t.report_template_id,
              title: t.report_template_name,
              sublabel: t.description ?? undefined,
              onAdd: () => assignTemplate(t.report_template_id),
              onPreview: () => setPreviewTemplateId(t.report_template_id),
            })
          )}
          assignedItems={visibleAttached.map((row) => {
            const display = resolveAssignedRowDisplay(
              row.report_template_id,
              activeTemplateIds,
              activeTemplateLookup,
              {
                title: row.report_template_name ?? `Template #${row.report_template_id}`,
                sublabel: row.description ?? undefined,
              }
            );

            return createElement(AssignAssignedRow, {
              key: row.report_template_id,
              title: display.title,
              sublabel: display.sublabel,
              isActive: display.isActive,
              listOrder: row.list_order,
              listLength: visibleAttached.length,
              onOrderChange: (newOrder: number) => handleOrderChange(row.report_template_id, newOrder),
              onRemove: () => unassignTemplate(row.report_template_id),
              onPreview: () => setPreviewTemplateId(row.report_template_id),
              showRequiredFields: true,
              isRequired: row.is_required,
              onRequiredChange: (checked: boolean) =>
                updateAttached(row.report_template_id, {
                  is_required: checked,
                  required_at: checked ? (row.required_at || todayDateOnly()) : null,
                }),
              requiredAt: toDateOnlyInputValue(row.required_at),
              onRequiredAtChange: (value: string) =>
                updateAttached(row.report_template_id, { required_at: value || null }),
              requiredSwitchId: `${copy.originLabel}-req-${row.report_template_id}`,
            });
          })}
        />
      )}

      <PreviewReportTemplateModal
        show={previewTemplateId != null}
        onHide={() => setPreviewTemplateId(null)}
        departmentId={selectedDepartmentId || undefined}
        reportTemplateId={previewTemplateId ?? undefined}
        {...previewScopeProps}
      />
    </>
  );
};

export default AssignReportTemplatesModalBody;
